import { useState } from "react";

import { api, type ScheduledActivity, type Step } from "@/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ErrorAlert } from "@/components/error-alert";
import { formatMinutes } from "@/lib/color";

type Row = { text: string; minutes: string };

function messageOf(err: unknown, fallback: string): string {
  return err instanceof Error && err.message ? err.message : fallback;
}

/**
 * Refine a block that has not started into a checklist of steps. Saving
 * writes the checklist into the Google Calendar event description and marks
 * the block as planned; saving an empty list clears it.
 */
export function ActivityStepsDialog({
  activity,
  onClose,
  onSaved,
}: {
  activity: ScheduledActivity;
  onClose: () => void;
  onSaved: () => Promise<void>;
}) {
  const [rows, setRows] = useState<Row[]>(() =>
    activity.steps && activity.steps.length > 0
      ? activity.steps.map((s) => ({
          text: s.text,
          minutes: s.minutes ? String(s.minutes) : "",
        }))
      : [{ text: "", minutes: "" }],
  );
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const blockMinutes = Math.round(
    (new Date(activity.end_at).getTime() -
      new Date(activity.start_at).getTime()) /
      60000,
  );
  const total = rows.reduce((s, r) => s + (Number(r.minutes) || 0), 0);

  function update(i: number, patch: Partial<Row>) {
    setRows((prev) => prev.map((r, j) => (j === i ? { ...r, ...patch } : r)));
  }

  function move(i: number, by: -1 | 1) {
    setRows((prev) => {
      const j = i + by;
      if (j < 0 || j >= prev.length) return prev;
      const next = [...prev];
      [next[i], next[j]] = [next[j], next[i]];
      return next;
    });
  }

  async function save(clear: boolean) {
    setBusy(true);
    setError(null);
    try {
      const steps: Step[] = clear
        ? []
        : rows
            .filter((r) => r.text.trim() !== "")
            .map((r) => ({
              text: r.text.trim(),
              minutes: Number(r.minutes) || undefined,
            }));
      await api.setActivitySteps(activity.id, steps);
      await onSaved();
    } catch (err) {
      setError(messageOf(err, "could not save the steps"));
      setBusy(false);
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-foreground/40 p-4 sm:p-8"
      role="dialog"
      aria-modal="true"
      aria-labelledby="activity-steps-title"
      onClick={(ev) => {
        if (ev.target === ev.currentTarget) onClose();
      }}
    >
      <div className="w-full max-w-md space-y-4 rounded-2xl border border-border bg-card p-5 shadow-xl">
        <div>
          <h2 id="activity-steps-title" className="text-lg font-semibold">
            Plan {activity.title}
          </h2>
          <p className="text-sm text-muted-foreground">
            Break the block into steps. They are added to the calendar event.
          </p>
        </div>

        <ul className="space-y-2">
          {rows.map((r, i) => (
            <li key={i} className="flex items-center gap-2">
              <Input
                className="min-w-0 flex-1"
                placeholder="e.g. Scales"
                value={r.text}
                maxLength={200}
                onChange={(ev) => update(i, { text: ev.target.value })}
                aria-label={`Step ${i + 1}`}
              />
              <Input
                className="w-20"
                type="number"
                min={0}
                max={480}
                placeholder="min"
                value={r.minutes}
                onChange={(ev) => update(i, { minutes: ev.target.value })}
                aria-label={`Minutes for step ${i + 1}`}
              />
              <Button
                variant="ghost"
                size="sm"
                onClick={() => move(i, -1)}
                disabled={i === 0}
                aria-label="Move up"
              >
                ↑
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => move(i, 1)}
                disabled={i === rows.length - 1}
                aria-label="Move down"
              >
                ↓
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() =>
                  setRows((prev) =>
                    prev.length === 1
                      ? [{ text: "", minutes: "" }]
                      : prev.filter((_, j) => j !== i),
                  )
                }
                aria-label="Remove step"
              >
                ✕
              </Button>
            </li>
          ))}
        </ul>

        <div className="flex items-center justify-between text-sm">
          <Button
            variant="outline"
            size="sm"
            disabled={rows.length >= 20}
            onClick={() => setRows((prev) => [...prev, { text: "", minutes: "" }])}
          >
            Add step
          </Button>
          <span
            className={
              total > blockMinutes
                ? "text-destructive"
                : "text-muted-foreground"
            }
          >
            {formatMinutes(total)} of {formatMinutes(blockMinutes)}
          </span>
        </div>

        {error && <ErrorAlert message={error} />}

        <div className="flex justify-between gap-2">
          <Button
            variant="ghost"
            disabled={busy || !activity.refined}
            onClick={() => void save(true)}
          >
            Clear plan
          </Button>
          <div className="flex gap-2">
            <Button variant="outline" disabled={busy} onClick={onClose}>
              Cancel
            </Button>
            <Button disabled={busy} onClick={() => void save(false)}>
              Save
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
